/**
 * Comparing a finished run against the global record for its exact
 * configuration.
 *
 * A personal best and the global record must never mean the same thing on
 * screen (`docs/decisions/0045`): a player once read the celebration for her
 * own best as the record everyone can see. This module is the pure
 * arithmetic and copy behind the sentence that states
 * the difference plainly, shared by `screens/ResultsScreen.svelte` (the run
 * that just finished) and `nav/MyScoresSheet.svelte` (every run on record).
 * No Svelte, no store — a `ModeConfig` plus two numbers in, a sentence out,
 * so it is unit-testable with no DOM at all.
 *
 * The unit a gap is expressed in is not carried on either side directly:
 * `PersonalBestScore`/`SoloRecordEntry` (`state/records-io.ts`) both give a
 * raw number whose meaning depends on which mode+goal produced it
 * (`packages/modes/src/sprint.ts`, `survival.ts`). Exactly one configuration
 * in this codebase scores a line count rather than elapsed time — Sprint's
 * own timed variant — so `isLinesMetric` is the one place that distinction is
 * read from the config, rather than threaded through as a parameter
 * everywhere else.
 */
import { formatClock } from '@interlock/modes';
import type { ModeConfig } from '@interlock/modes';

export type RecordGapDirection = 'ahead' | 'behind' | 'tied';

export interface RecordComparison {
  readonly direction: RecordGapDirection;
  /** The raw magnitude, formatted in the metric's own unit ("20.2s", "12 lines") — empty when tied. */
  readonly amountText: string;
}

/**
 * Sprint's timed variant scores a line count ("42 lines"); every other
 * configuration this codebase has — Sprint's own lines-goal variant,
 * Survival — scores elapsed time (`formatClock`). `config.goal` is `undefined`
 * for Survival, which reads as "not the timed Sprint variant" correctly by
 * construction.
 */
export function isLinesMetric(config: ModeConfig): boolean {
  return config.modeId === 'sprint' && config.goal?.kind === 'time';
}

function formatMagnitude(value: number, config: ModeConfig): string {
  return isLinesMetric(config)
    ? `${String(value)} line${value === 1 ? '' : 's'}`
    : formatClock(value);
}

/**
 * Compares two raw scores of the identical metric (same mode key). A
 * positive `diff` always means "behind", regardless of which direction
 * `betterWhen` counts as better — that normalisation is the entire point of
 * this function existing rather than inlining the subtraction at each call
 * site.
 */
export function compareToRecord(
  ownValue: number,
  recordValue: number,
  betterWhen: 'lower' | 'higher',
  config: ModeConfig,
): RecordComparison {
  const diff = betterWhen === 'lower' ? ownValue - recordValue : recordValue - ownValue;
  if (diff === 0) return { direction: 'tied', amountText: '' };
  return {
    direction: diff > 0 ? 'behind' : 'ahead',
    amountText: formatMagnitude(Math.abs(diff), config),
  };
}

/** True when `ownValue` genuinely improves on — not merely ties — `recordValue`. */
export function beatsRecord(
  ownValue: number,
  recordValue: number,
  betterWhen: 'lower' | 'higher',
): boolean {
  return betterWhen === 'lower' ? ownValue < recordValue : ownValue > recordValue;
}

/**
 * The results screen's own record-row sentence, and `nav/MyScoresSheet.svelte`'s
 * per-entry one. `record === null` means nobody has set one yet for this
 * exact configuration — not "unknown"; callers only reach this once the
 * records store has actually answered (`records.status === 'ready'`), the
 * same distinction `docs/decisions/0039` draws for the start page's own
 * record line.
 *
 * Deliberately has no special case for "the holder is you" — `record.username`
 * is compared and printed exactly the same whoever it names, which is
 * correct: a tie against your own previous record and a tie against a
 * stranger's are the identical sentence, "you match the record."
 */
export function describeRecordGap(
  own: { readonly value: number; readonly betterWhen: 'lower' | 'higher' },
  record: { readonly result: number; readonly username: string } | null,
  config: ModeConfig,
): string {
  if (record === null) return 'Nobody holds this record yet — be the first.';
  const comparison = compareToRecord(own.value, record.result, own.betterWhen, config);
  switch (comparison.direction) {
    case 'behind':
      return `You're ${comparison.amountText} behind ${record.username}.`;
    case 'ahead':
      return `You're ${comparison.amountText} ahead of ${record.username}.`;
    case 'tied':
      return `You match the record, held by ${record.username}.`;
  }
}
